import { BaseWidget } from 'angular-instantsearch';
import { EventEmitter, Output, Input, Directive } from '@angular/core';
import { CatalogFilter } from '@app/shared/model/interfaces/catalog';
import { ContentsRouteParameter, CatalogFacetLink } from '@vestiaire/api-sdk-angular';
import {
  parseItemLabelAndId,
  checkRouteParameters,
  FACET_LINKS_MAPPING,
  ToggleFacets
} from '@app/shared/components/catalog/services/filters.service';
import { isPlatformServer } from '@angular/common';

export interface IBaseItem {
  id?: number; // Unique ID
  label: string; // Label to print out
  value?: string; // Value to injext in URI
  compound_values?: string[];
  count?: string; // Count values
  isRefined?: boolean; // Indicates if filter item is selected
  default?: boolean;
  url?: string; // For SEO only
}

export interface IUriList {
  facetId: string;
  path: string;
}

@Directive()
export abstract class CatalogWidgetDirective extends BaseWidget {
  @Input()
  options: CatalogFilter;
  @Input()
  pageRouteParameter: ContentsRouteParameter[];
  @Input()
  facetLink: CatalogFacetLink;
  @Input()
  showRefinementBody: boolean;

  @Output()
  refined = new EventEmitter<boolean>();

  @Output()
  clickFilter = new EventEmitter<any>();

  state: {
    items?: IBaseItem[];
    refine: Function;
  };

  isServer: boolean;

  constructor(widgetName: string, platformId?: object) {
    super(widgetName);
    this.isServer = platformId ? isPlatformServer(platformId) : false;
  }

  // Returns list of Items that will be used to build the filters
  // TODO: This function is called whenever scrolling. Shouldn't be the case
  get items(): IBaseItem[] {
    const isHierarchical = this.options && this.options.isHierarchical;
    let facetName = null;

    if (this.options) {
      facetName = Array.isArray(this.options.facet) ? this.options.facet[0] : this.options.facet;
      if (ToggleFacets.indexOf(facetName) === -1) {
        facetName = null;
      }
    }

    if (this.isServer) {
      return this.transformItems(isHierarchical, facetName);
    } else {
      // Merge duplicates
      return this.mergeSameLabels(this.transformItems(isHierarchical, facetName));
    }
  }

  /**
   * mergeSameLabels takes in an array of IBaseItem and checks if
   * some items have the same labels. If they do it then merges those items
   * into a single one
   * @protected
   * @memberof CatalogWidget
   */
  protected mergeSameLabels(filterItems: IBaseItem[]): IBaseItem[] {
    const dups = new Map();

    filterItems.forEach((flt, i) => {
      const formattedFilter = flt.label.toLocaleLowerCase().trim();

      if (!dups.has(formattedFilter)) {
        dups.set(formattedFilter, {
          ...flt,
          originalID: i,
          compound_values: [flt.value]
        });
        return;
      }

      const dup = dups.get(formattedFilter);

      filterItems[dup.originalID] = {
        ...flt,
        count: dup.count + flt.count,
        compound_values: [].concat(dup.compound_values, [flt.value])
      };

      // Not splicing in order to keep original index order
      filterItems[i] = null;
    });

    return filterItems.filter(v => v != null);
  }

  /**
   * transformItems formats the items for printing out into filters
   * @returns {IBaseItem[]}
   * @memberof CatalogWidget
   */
  protected transformItems(isHierarchical: boolean, facetName: string): IBaseItem[] {
    const transformedItems = [];
    const filterUrlList = isPlatformServer ? this.getUrlList() : null;

    for (const item of this.state.items || []) {
      const parsedItem = parseItemLabelAndId(String(item.label), isHierarchical, facetName, filterUrlList);
      if (this.isSeoUrlHidden(parseInt(item.count, 10))) {
        parsedItem.url = null;
      }

      if (this.options && checkRouteParameters(this.options.routeParameters, this.pageRouteParameter, item)) {
        transformedItems.push({ ...item, ...parsedItem });
        continue;
      }
      transformedItems.push({ ...item, ...parsedItem });
    }

    return transformedItems;
  }

  /**
   *  return a boolean in order to known if we have to hidden the link to the facet
   *  return true if less than 4 products
   *  return true if material or color facet in lifestyle/kid catalog page
   *  otherwise return false
   * @param count
   */
  isSeoUrlHidden(count: number) {
    // key is universe id and value is array of facet label
    const facetLinksToHide = {
      '3': ['CATALOG.FILTERS.MATERIALS', 'CATALOG.FILTERS.COLORS'], // no materials/colors links on kid universe
      '4': ['CATALOG.FILTERS.MATERIALS', 'CATALOG.FILTERS.COLORS'] // no materials/colors links on kid universe
    };
    return (
      count < 4 ||
      (this.pageRouteParameter &&
        this.pageRouteParameter[0] &&
        this.options.label &&
        facetLinksToHide[this.pageRouteParameter[0].value] &&
        facetLinksToHide[this.pageRouteParameter[0].value].includes(this.options.label))
    );
  }

  public refine(event: MouseEvent, item: IBaseItem) {
    event.preventDefault();
    event.stopPropagation();

    item.isRefined = !item.isRefined;
    // refine through Algolia API
    if (item.compound_values && item.compound_values.length > 0) {
      item.compound_values.forEach(v => this.state.refine(v));
    } else {
      this.state.refine(item.value);
    }

    this.refined.emit(true);

    // dispatch APPLY_FILTER action to send analytics
    if (item.isRefined) {
      this.clickFilter.emit({ filterItem: item, filterOptions: this.options });
    }
  }

  /**
   * Return Url list of current filter items
   * Please note that this method will only return items under certain
   * conditions ( such as beign on a specific brand )
   * Also note that this method is usefull for SSR only as it is used
   * for SEO purpose only
   */
  public getUrlList(): null | IUriList[] {
    let result = null;

    if (this.facetLink && this.facetLink[FACET_LINKS_MAPPING[this.options.label]]) {
      result = this.facetLink[FACET_LINKS_MAPPING[this.options.label]];
    }

    return result;
  }

  public toggleShowBody() {
    this.showRefinementBody = !this.showRefinementBody;
  }
}
